import { $fetch } from 'utils/fetch'

/**
 * Knowledge Base API utility functions
 */

/**
 * Create a new knowledge base from a file
 * @param {object} data - The request data
 * @param {string} data.file_id - ID of the file to process
 * @param {string} data.name - Name for the knowledge base
 * @param {string} data.description - Optional description
 * @returns {Promise} API response
 */
export async function createKnowledgeBase(data) {
  return $fetch('/v1/knowledge/learn', {
    method: 'POST',
    body: data,
  })
}

/**
 * Get all knowledge bases for the organization
 * @param {object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 20)
 * @returns {Promise} API response with list of KBs
 */
export async function getKnowledgeBases({ page = 1, limit = 20 } = {}) {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  })

  return $fetch(`/v1/knowledge/?${params}`)
}

/**
 * Get a specific knowledge base by ID
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @returns {Promise} API response with KB data
 */
export async function getKnowledgeBase(knowledgeBaseId) {
  return $fetch(`/v1/knowledge/${knowledgeBaseId}`)
}

/**
 * Update a knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {object} data - Update data
 * @param {string} data.name - New name (optional)
 * @param {string} data.description - New description (optional)
 * @returns {Promise} API response with updated KB
 */
export async function updateKnowledgeBase(knowledgeBaseId, data) {
  return $fetch(`/v1/knowledge/${knowledgeBaseId}`, {
    method: 'PUT',
    body: data,
  })
}

/**
 * Delete a knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @returns {Promise} API response
 */
export async function deleteKnowledgeBase(knowledgeBaseId) {
  return $fetch(`/v1/knowledge/${knowledgeBaseId}`, {
    method: 'DELETE',
  })
}

/**
 * Query the knowledge base
 * @param {object} data - Query data
 * @param {string} data.query - The question/query to ask
 * @param {string} data.document_id - Optional specific document ID
 * @param {number} data.top_k - Number of results to return (default: 3)
 * @returns {Promise} API response with query results
 */

export async function queryKnowledgeBase(data) {
  return $fetch('/v1/knowledge/chat', {
    method: 'POST',
    body: data,
  })
}

/**
 * Submit feedback for KB responses
 * @param {object} data - Feedback data
 * @param {string} data.question - The original question
 * @param {string} data.answer - The answer provided
 * @param {string} data.document_id - Document ID
 * @param {string} data.chunk_id - Optional chunk ID
 * @param {number} data.confidence_score - Confidence score (0-1)
 * @returns {Promise} API response
 */
export async function submitKnowledgeBaseFeedback(data) {
  return $fetch('/v1/knowledge/feedback', {
    method: 'POST',
    body: data,
  })
}

/**
 * Get document information
 * @param {string} documentId - Document ID
 * @returns {Promise} API response with document info
 */
export async function getDocumentInfo(documentId) {
  return $fetch(`/v1/knowledge/documents/${documentId}/info`)
}

/**
 * Delete a document from knowledge base
 * @param {string} documentId - Document ID
 * @returns {Promise} API response
 */
export async function deleteDocument(documentId) {
  return $fetch(`/v1/knowledge/documents/${documentId}`, {
    method: 'DELETE',
  })
}

/**
 * Get knowledge base status (replaces getProcessingStatus)
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @returns {Promise} API response with KB data including status
 */
export async function getKnowledgeBaseStatus(knowledgeBaseId) {
  return $fetch(`/v1/knowledge/${knowledgeBaseId}`)
}

// Legacy function for backwards compatibility
export const getProcessingStatus = getKnowledgeBaseStatus
